import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n/I18nProvider';

const api = {
  get: async (endpoint) => {
    const token = localStorage.getItem('token');
    const response = await fetch(endpoint, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error('Network response was not ok');
    return response.json();
  },
  put: async (endpoint, body) => {
    const token = localStorage.getItem('token');
    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Request failed');
    }
    return response.json();
  }
};

export const COACH_PROFILE_DEFAULTS = Object.freeze({
  coach_language: 'zh',
  target_ielts_band: '6',
  coaching_preference: '',
});

const isValidTargetBand = (value) => {
  if (value === '' || value === null || value === undefined) return false;
  const band = Number(value);
  return Number.isFinite(band) && band >= 0 && band <= 9 && Number.isInteger(band * 2);
};

export const normalizeCoachProfile = (user = {}) => ({
  coach_language: user.coach_language === 'en' ? 'en' : COACH_PROFILE_DEFAULTS.coach_language,
  target_ielts_band: isValidTargetBand(user.target_ielts_band)
    ? String(user.target_ielts_band)
    : COACH_PROFILE_DEFAULTS.target_ielts_band,
  coaching_preference: typeof user.coaching_preference === 'string'
    && user.coaching_preference.length <= 200
    ? user.coaching_preference
    : COACH_PROFILE_DEFAULTS.coaching_preference,
});

export const buildCoachProfilePayload = (settings) => ({
  coach_language: settings.coach_language,
  target_ielts_band: Number(settings.target_ielts_band),
  coaching_preference: settings.coaching_preference,
});

export const validateCoachProfile = (settings) => {
  if (!['zh', 'en'].includes(settings?.coach_language)) {
    return 'Coach language must be zh or en.';
  }
  if (!isValidTargetBand(settings?.target_ielts_band)) {
    return 'Target IELTS band must be between 0 and 9 in 0.5 increments.';
  }
  if (typeof settings?.coaching_preference !== 'string' || settings.coaching_preference.length > 200) {
    return 'Coaching preference must be text with 200 characters or fewer.';
  }
  return '';
};

const UserProfile = ({ user, isLoading, error, t }) => {
  if (isLoading) return <p>{t('Loading...')}</p>;
  if (error) return <p className="text-red-500">{t(error)}</p>;

  return (
    <div className="bg-white p-6 rounded-xl shadow-md mb-8">
      <h3 className="text-xl font-bold mb-4 text-gray-700">{t('User Info')}</h3>
      <p><span className="font-semibold">{t('Username:')}</span> <span data-i18n-skip>{user.username}</span></p>
      <p><span className="font-semibold">{t('Nickname:')}</span> <span data-i18n-skip>{user.nickname || t('Not set')}</span></p>
      <div className="mt-2">
        <p className="font-semibold">{t('Classes:')}</p>
        {Array.isArray(user.classes) && user.classes.length > 0 ? (
          <ul className="list-disc list-inside text-gray-700">
            {user.classes.map(c => (
              <li data-i18n-skip key={c.id}>{c.name || c.id}</li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">{t('No classes')}</p>
        )}
      </div>
    </div>
  );
};

export const IeltsCoachPreferencesForm = ({ user, onProfileUpdate, t }) => {
  const [settings, setSettings] = useState(() => normalizeCoachProfile(user));
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setSettings(normalizeCoachProfile(user));
  }, [user?.coach_language, user?.target_ielts_band, user?.coaching_preference]);

  const handleChange = (event) => {
    setSettings(current => ({ ...current, [event.target.name]: event.target.value }));
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationError = validateCoachProfile(settings);
    if (validationError) {
      setError(validationError);
      setSuccess('');
      return;
    }

    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      await api.put('/api/user/profile', buildCoachProfilePayload(settings));
      setSuccess('IELTS coaching preferences saved!');
      await onProfileUpdate?.();
    } catch (requestError) {
      setError(requestError.message || 'Request failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const bandOptions = Array.from({ length: 19 }, (_, index) => {
    const band = index / 2;
    return band % 1 === 0 ? String(band) : band.toFixed(1);
  });

  return (
    <section className="bg-white p-6 rounded-xl shadow-md mb-8" aria-label={t('IELTS speaking coaching preferences')}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-xl font-bold text-gray-700">{t('IELTS speaking coaching preferences')}</h3>
          <p className="mt-1 text-sm leading-6 text-gray-600">{t('Set the language, target band, and focus for your IELTS Speaking coaching.')}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-5 space-y-5">
        <fieldset>
          <legend className="text-sm font-semibold text-gray-800">{t('Coach language')}</legend>
          <p className="mt-1 text-xs text-gray-500">{t('Choose whether coaching feedback is mainly in Chinese or English.')}</p>
          <div className="mt-2 inline-flex max-w-full flex-wrap rounded-md border border-gray-300 bg-gray-50 p-1" role="group" aria-label={t('Coach language')}>
            {[
              ['zh', 'Chinese'],
              ['en', 'English'],
            ].map(([value, label]) => (
              <label key={value} className={`cursor-pointer rounded px-3 py-2 text-sm font-semibold ${settings.coach_language === value ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-white'}`}>
                <input
                  type="radio"
                  name="coach_language"
                  value={value}
                  checked={settings.coach_language === value}
                  onChange={handleChange}
                  className="sr-only"
                />
                {t(label)}
              </label>
            ))}
          </div>
        </fieldset>

        <label className="block text-sm font-semibold text-gray-800" htmlFor="target-ielts-band">
          {t('Target IELTS band')}
          <select
            id="target-ielts-band"
            name="target_ielts_band"
            value={settings.target_ielts_band}
            onChange={handleChange}
            className="mt-1 block w-full rounded border border-gray-300 bg-white px-3 py-2 font-normal sm:max-w-48"
          >
            {bandOptions.map(band => <option value={band} key={band}>{band}</option>)}
          </select>
          <span className="mt-1 block text-xs font-normal text-gray-500">{t('Choose a band from 0 to 9 in 0.5 increments.')}</span>
        </label>

        <label className="block text-sm font-semibold text-gray-800" htmlFor="coaching-preference">
          {t('Coaching preference')}
          <textarea
            id="coaching-preference"
            name="coaching_preference"
            value={settings.coaching_preference}
            onChange={handleChange}
            maxLength={200}
            rows={3}
            className="mt-1 block w-full resize-y rounded border border-gray-300 bg-white px-3 py-2 font-normal"
            placeholder={t('For example: focus on fluency, linking words, and concise corrections.')}
          />
          <span className="mt-1 block text-right text-xs font-normal text-gray-500">
            {t('{count}/200 characters', { count: settings.coaching_preference.length })}
          </span>
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <button type="submit" disabled={isSubmitting} className="rounded-lg bg-blue-600 px-4 py-2.5 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300">
            {isSubmitting ? t('Saving...') : t('Save IELTS coaching preferences')}
          </button>
          {error && <p className="text-sm text-red-600" role="alert">{t(error)}</p>}
          {success && <p className="text-sm text-green-600" role="status">{t(success)}</p>}
        </div>
      </form>
    </section>
  );
};

const UpdateNicknameForm = ({ onProfileUpdate, t }) => {
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      await api.put('/api/user/profile', { nickname });
      setSuccess('Nickname updated!');
      onProfileUpdate();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md mb-8">
      <h3 className="text-xl font-bold mb-4 text-gray-700">{t('Update Nickname')}</h3>
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder={t('Enter new nickname')}
          className="w-full p-3 rounded bg-gray-50 border border-gray-300 mb-4"
          required
        />
        <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg">
          {isSubmitting ? t('Saving...') : t('Save')}
        </button>
        {error && <p className="mt-3 text-red-600">{t(error)}</p>}
        {success && <p className="mt-3 text-green-600">{t(success)}</p>}
      </form>
    </div>
  );
};

const UpdatePasswordForm = ({ t }) => {
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setPasswords({ ...passwords, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      setError('New password does not match.');
      return;
    }
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      await api.put('/api/user/profile', {
        current_password: passwords.currentPassword,
        new_password: passwords.newPassword
      });
      setSuccess('Password updated!');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md">
      <h3 className="text-xl font-bold mb-4 text-gray-700">{t('Change Password')}</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="password"
          name="currentPassword"
          value={passwords.currentPassword}
          onChange={handleChange}
          placeholder={t('Current password')}
          className="w-full p-3 rounded bg-gray-50 border border-gray-300"
          required
        />
        <input
          type="password"
          name="newPassword"
          value={passwords.newPassword}
          onChange={handleChange}
          placeholder={t('New password')}
          className="w-full p-3 rounded bg-gray-50 border border-gray-300"
          required
        />
        <input
          type="password"
          name="confirmPassword"
          value={passwords.confirmPassword}
          onChange={handleChange}
          placeholder={t('Confirm new password')}
          className="w-full p-3 rounded bg-gray-50 border border-gray-300"
          required
        />
        <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg">
          {isSubmitting ? t('Updating...') : t('Update Password')}
        </button>
        {error && <p className="mt-3 text-red-600">{t(error)}</p>}
        {success && <p className="mt-3 text-green-600">{t(success)}</p>}
      </form>
    </div>
  );
};

const UpdateSecurityAnswerForm = ({ t }) => {
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      await api.put('/api/user/profile', { security_answer: answer });
      setSuccess('Security answer updated!');
      setAnswer('');
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-xl shadow-md mt-8">
      <h3 className="text-xl font-bold mb-2 text-gray-700">{t('Security Question')}</h3>
      <p className="text-sm text-gray-600 mb-4">{t('What is your favorite animal?')}</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder={t('Enter answer (used for password recovery)')}
          className="w-full p-3 rounded bg-gray-50 border border-gray-300"
          required
        />
        <button type="submit" disabled={isSubmitting} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg">
          {isSubmitting ? t('Saving...') : t('Save Answer')}
        </button>
        {error && <p className="mt-3 text-red-600">{t(error)}</p>}
        {success && <p className="mt-3 text-green-600">{t(success)}</p>}
      </form>
    </div>
  );
};

const StudentProfilePage = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const [user, setUser] = useState({ username: '', nickname: '', ...COACH_PROFILE_DEFAULTS });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUserProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.get('/api/user/profile');
      setUser(data);
      setError('');
    } catch (err) {
      setError('Unable to load profile.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  return (
    <div className="max-w-2xl mx-auto">
      <button
        onClick={() => navigate(-1)}
        className="mb-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded-lg text-sm transition duration-300"
      >
        {t('← Back')}
      </button>
      <UserProfile user={user} isLoading={isLoading} error={error} t={t} />
      <IeltsCoachPreferencesForm user={user} onProfileUpdate={fetchUserProfile} t={t} />
      {/* Only allow updating nickname if nickname is missing or empty */}
      {(!user?.nickname || String(user.nickname).trim() === '') && (
        <UpdateNicknameForm onProfileUpdate={fetchUserProfile} t={t} />
      )}
      <UpdatePasswordForm t={t} />
      <UpdateSecurityAnswerForm t={t} />
    </div>
  );
};

export default StudentProfilePage;
